import React, { ReactNode } from "react";
import { cn } from "../../utils";

export interface DashboardCardInfoItem {
  label: string;
  value: ReactNode;
  icon: React.ElementType;
  iconColor?: string;
  valueColor?: string;
}

export interface BaseDashboardCardProps {
  idLabel: string;
  statusBadge: ReactNode;
  title: string;
  infoItems: DashboardCardInfoItem[];
  footer: ReactNode;
  onClick?: () => void;
  className?: string;
}

export const BaseDashboardCard = React.memo(function BaseDashboardCard({
  idLabel,
  statusBadge,
  title,
  infoItems,
  footer,
  onClick,
  className,
}: BaseDashboardCardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "w-full group backdrop-blur-xl border rounded-[24px] p-4 sm:p-6 md:p-7 transition-all duration-300 cursor-pointer bg-white/80 border-gray-100 hover:shadow-[0_12px_32px_-4px_rgba(46,127,238,0.15)] hover:border-primary/50",
        className,
      )}
    >
      {/* Header Section */}
      <div className="mb-4 sm:mb-5">
        <div className="flex items-center gap-2 mb-2 sm:mb-3">
          <div className="h-[28px] px-2.5 backdrop-blur-sm rounded-[8px] border flex items-center bg-gray-50/80 border-gray-100">
            <span className="font-medium text-2xs text-gray-500 uppercase tracking-tight">
              {idLabel}
            </span>
          </div>
          {statusBadge}
        </div>
        <h3 className="text-lg sm:text-xl md:text-2xl font-semibold leading-tight transition-colors line-clamp-2 text-gray-900 group-hover:text-primary/90">
          {title}
        </h3>
      </div>

      {/* Info Section */}
      <div className="mb-4 sm:mb-5">
        <div className="backdrop-blur-sm rounded-[16px] p-3 sm:p-4 bg-gray-50/50">
          {infoItems.map((item, index) => (
            <div
              key={item.label}
              className={cn(
                "flex items-center justify-between py-2.5",
                index !== infoItems.length - 1 && "border-b border-gray-200/50",
              )}
            >
              <div className="flex items-center gap-2.5">
                <item.icon
                  className={cn("w-4 h-4", item.iconColor ?? "text-primary")}
                />
                <span className="text-sm font-normal text-gray-500">
                  {item.label}
                </span>
              </div>
              <span
                className={cn(
                  "text-sm font-medium text-gray-900",
                  item.valueColor,
                )}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Section */}
      <div className="flex gap-2">{footer}</div>
    </div>
  );
});

BaseDashboardCard.displayName = "BaseDashboardCard";
